import React, { useState, useRef, useContext } from 'react';
import { Upload, Button, Card } from 'antd';
import { sparkToast, ReadOnlyGuard } from '@spark';
import { DownloadOutlined } from '@ant-design/icons';
import { get_base_url, post_formdata_with_progress, put_blob_with_progress } from '@/shared/http/client';
import { TaskType } from '@/shared/domain/taskTypes';
import { checkDatasetExists } from '@/features/datasets/api/datasetApi';
import DatasetUploadStepsModal from '@/features/datasets/components/DatasetUploadStepsModal';
import DatasetExistsModal from '@/features/datasets/components/DatasetExistsModal';
import RenameUploadModal from '@/features/datasets/components/RenameUploadModal';
import { useAuth } from '@/features/auth/AuthContext';
import { colors } from '@/shared/tokens/colors';
import { GlobalContext } from '@/app/providers/globalState';
import { showNetworkError, showUploadError } from '@/features/datasets/utils/uploadUtils';
import {
    initTwoPhaseProgressItem,
    updateTwoPhaseUploadProgress,
    markTwoPhaseUploadCompleteStartProcessing,
    markTwoPhaseFailed,
} from '@/features/datasets/utils/uploadProgress';
import { detectFileFormat } from '@/features/datasets/utils/filePreviewUtils';

const ExternalModelResultsUpload = ({
    currentUsecase,
    allLabelOptions = null,
    onUploadSuccess
}) => {
    const { role } = useAuth();
    const { setProgressStatus, registerUploadAbort, unregisterUploadAbort, clearCancelledUploadSuppression } = useContext(GlobalContext);
    const fileInputRef = useRef(null);
    const [uploadedModelFileList, setUploadedModelFileList] = useState([]);
    const [showPredictionsModal, setShowPredictionsModal] = useState(false);
    const [pendingPredictionsFile, setPendingPredictionsFile] = useState(null);
    const [predictionsFileContent, setPredictionsFileContent] = useState(null);
    const [predictionsFileType, setPredictionsFileType] = useState(null);
    const [predictionsUploading, setPredictionsUploading] = useState(false);
    const [predictionsSelectedOrigin, setPredictionsSelectedOrigin] = useState(null);
    const [predictionsDelimiter, setPredictionsDelimiter] = useState(null);
    const [showDatasetExistsModal, setShowDatasetExistsModal] = useState(false);
    const [showRenameModal, setShowRenameModal] = useState(false);
    const [renamedPredictionsFileName, setRenamedPredictionsFileName] = useState(null);
    const [isDragOver, setIsDragOver] = useState(false);

    // File types allowed for manual model predictions (same as datasets)
    const filesAllowed = currentUsecase?.task_type === TaskType.classification ? '.csv,.json,.jsonl,.parquet' : '.json,.jsonl,.parquet';

    // Maximum file size to read into memory (10MB) - enough for column extraction
    // For very large files, we only read a sample to avoid browser crashes
    const MAX_FILE_CHUNK_SIZE = 10 * 1024 * 1024; // 10MB

    const readFileContent = async (file, options = {}) => {
        const { chunkOnly = false } = options;
        const format = detectFileFormat(file);
        const isBinary = format === 'parquet';

        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onerror = reject;

            if (isBinary) {
                reader.onload = (e) => {
                    const bytes = new Uint8Array(e.target.result);
                    let binary = '';
                    for (let i = 0; i < bytes.length; i++) {
                        binary += String.fromCharCode(bytes[i]);
                    }
                    resolve(btoa(binary));
                };
                if (chunkOnly && file.size > MAX_FILE_CHUNK_SIZE) {
                    reader.readAsArrayBuffer(file.slice(0, MAX_FILE_CHUNK_SIZE));
                } else {
                    reader.readAsArrayBuffer(file);
                }
            } else {
                reader.onload = (e) => {
                    const content = e.target.result;
                    const base64Content = btoa(unescape(encodeURIComponent(content)));
                    resolve(base64Content);
                };
                if (chunkOnly && file.size > MAX_FILE_CHUNK_SIZE) {
                    reader.readAsText(file.slice(0, MAX_FILE_CHUNK_SIZE));
                } else {
                    reader.readAsText(file);
                }
            }
        });
    };

    const handlePredictionsModalConfirm = async (
        datasetOrigin,
        labelMappings = {},
        exampleColumnName = "example",
        labelColumnName = "labels",
        relationsColumnName = "",
        relationsMappings = {}
    ) => {
        if (!pendingPredictionsFile || !currentUsecase) {
            setShowPredictionsModal(false);
            return;
        }
        setPredictionsUploading(true);

        const progressKey = renamedPredictionsFileName || (pendingPredictionsFile?.name || '').split('.')[0];
        if (progressKey) {
            clearCancelledUploadSuppression?.(progressKey);
            initTwoPhaseProgressItem({
                setProgressStatus,
                key: progressKey,
                datasetName: progressKey,
                usecaseId: currentUsecase?._id,
                message: 'Uploading file',
            });
        }

        try {
            const formData = new FormData();
            // Use renamed filename if provided
            let fileToUpload = pendingPredictionsFile;
            if (renamedPredictionsFileName) {
                const fileExtension = pendingPredictionsFile.name.split('.').pop();
                const newFileName = `${renamedPredictionsFileName}.${fileExtension}`;
                fileToUpload = new File([pendingPredictionsFile], newFileName, { type: pendingPredictionsFile.type });
            }
            formData.append('client_id', currentUsecase?.client_id || "40b3a00e282ee2db1338c3bba881b99125fc3ba1d7f5c29232ce1ad63fab1e75");
            formData.append('file_type', predictionsFileType || 'csv');
            formData.append('is_benchmark', false);
            formData.append('is_manual_model_predictions', true);
            formData.append('task_type', currentUsecase?.task_type || TaskType.none);
            if (currentUsecase?._id) formData.append('usecase_id', currentUsecase._id);
            if (datasetOrigin && datasetOrigin !== 'skip') {
                formData.append('data_creation_type', datasetOrigin);
            }
            formData.append('label_mappings', JSON.stringify(labelMappings || {}));
            formData.append('example_column_name', exampleColumnName || "example");
            formData.append('label_column_name', labelColumnName || "labels");
            if (relationsColumnName) {
                formData.append('relations_column_name', relationsColumnName);
            }
            if (relationsMappings && Object.keys(relationsMappings).length > 0) {
                formData.append('relations_mappings', JSON.stringify(relationsMappings));
            }
            if (predictionsDelimiter) {
                formData.append('classification_labels_delimiter', predictionsDelimiter);
            }

            const base = get_base_url(window.location.origin);
            const createFd = new FormData();
            createFd.append('client_id', currentUsecase?.client_id || "40b3a00e282ee2db1338c3bba881b99125fc3ba1d7f5c29232ce1ad63fab1e75");
            createFd.append('filename', fileToUpload.name);
            createFd.append('content_type', fileToUpload.type || 'application/octet-stream');
            // Link the upload to its task so auto-train counts it as an active
            // flow from the first moment (see datasetUploadFlow.js).
            if (currentUsecase?._id) {
                createFd.append('usecase_id', currentUsecase._id);
            }

            const created = await post_formdata_with_progress(`${base}/create-dataset-upload-url`, createFd, {
                withCredentials: true,
            });
            if (created.aborted) {
                return;
            }
            if (!created.ok) {
                let errorDetail = '';
                try {
                    const errorData = created?.data;
                    errorDetail = typeof errorData === 'string'
                        ? errorData
                        : (errorData?.detail || errorData?.message || created?.rawText || '');
                } catch {
                    errorDetail = 'Unknown error occurred';
                }
                showUploadError(pendingPredictionsFile?.name || 'file', errorDetail);
                if (progressKey) {
                    markTwoPhaseFailed({
                        setProgressStatus,
                        key: progressKey,
                        errorMessage: errorDetail,
                        details: errorDetail,
                    });
                }
                return;
            }

            const putResp = await put_blob_with_progress(
                created.data.upload_url,
                fileToUpload,
                created.data.content_type,
                {
                    onUploadProgress: ({ percent }) => {
                        if (!progressKey) return;
                        if (percent == null) return;
                        updateTwoPhaseUploadProgress({
                            setProgressStatus,
                            key: progressKey,
                            uploadPercent: percent,
                        });
                    },
                    onAbortReady: (abortFn) => {
                        if (progressKey) registerUploadAbort(progressKey, abortFn);
                    },
                }
            );
            if (putResp.aborted) {
                return;
            }
            if (!putResp.ok) {
                const errorDetail = putResp.rawText || `Signed upload failed (${putResp.status})`;
                showUploadError(pendingPredictionsFile?.name || 'file', errorDetail);
                if (progressKey) {
                    markTwoPhaseFailed({
                        setProgressStatus,
                        key: progressKey,
                        errorMessage: errorDetail,
                        details: errorDetail,
                    });
                }
                return;
            }

            formData.append('object_key', created.data.object_key);
            formData.append('status_id', created.data.status_id);
            formData.append('upload_filename', fileToUpload.name);
            formData.append('signed_content_type', created.data.content_type);

            const response = await post_formdata_with_progress(`${base}/complete-dataset-upload`, formData, {
                withCredentials: true,
            });

            if (response.aborted) {
                return;
            }

            if (!response.ok) {
                let errorDetail = '';
                try {
                    const errorData = response?.data;
                    if (typeof errorData === 'string') {
                        errorDetail = errorData;
                    } else {
                        errorDetail = errorData?.detail || errorData?.message || '';
                    }
                } catch (parseError) {
                    try {
                        errorDetail = response?.rawText || '';
                    } catch (textError) {
                        errorDetail = 'Unknown error occurred';
                    }
                }
                showUploadError(pendingPredictionsFile?.name || 'file', errorDetail);
                if (progressKey) {
                    markTwoPhaseFailed({
                        setProgressStatus,
                        key: progressKey,
                        errorMessage: errorDetail,
                        details: errorDetail,
                    });
                }
            } else {
                if (progressKey) {
                    markTwoPhaseUploadCompleteStartProcessing({ setProgressStatus, key: progressKey });
                }
                if (onUploadSuccess) {
                    onUploadSuccess();
                }
            }
        } catch (e) {
            showNetworkError();
            if (progressKey) {
                markTwoPhaseFailed({
                    setProgressStatus,
                    key: progressKey,
                    errorMessage: e?.message || 'Network error occurred during upload.',
                    details: e?.message,
                });
            }
        } finally {
            if (progressKey) unregisterUploadAbort(progressKey);
            setPredictionsUploading(false);
            setShowPredictionsModal(false);
            setPendingPredictionsFile(null);
            setRenamedPredictionsFileName(null);
            setUploadedModelFileList([]);
        }
    };

    const handleFileSelect = async (file) => {
        setUploadedModelFileList([file]);
        setPendingPredictionsFile(file);
        setPredictionsFileType(detectFileFormat(file) || 'json');

        try {
            // Read file content - only read first chunk if file is very large
            const shouldSampleContent = file.size > MAX_FILE_CHUNK_SIZE;
            const content = await readFileContent(file, { chunkOnly: shouldSampleContent });
            setPredictionsFileContent(content);

            // Check if dataset already exists before proceeding
            const datasetName = (file.name || '').split('.')[0];
            const datasetExists = await checkDatasetExists(datasetName);

            if (datasetExists) {
                setShowDatasetExistsModal(true);
            } else {
                setShowPredictionsModal(true);
            }
        } catch (err) {
            console.error('Error reading file:', err);
            sparkToast.error('Error reading file content');
        }
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (role === 'Viewer') return;
        setIsDragOver(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (role === 'Viewer') return;
        setIsDragOver(false);
    };

    const handleDrop = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (role === 'Viewer') return;
        setIsDragOver(false);
        const file = e.dataTransfer.files[0];
        if (file) {
            await handleFileSelect(file);
        }
    };

    const handleFileInputChange = async (event) => {
        const file = event.target.files[0];
        if (file) {
            await handleFileSelect(file);
        }
        // Reset the input so the same file can be selected again
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    return (
        <>
            <ReadOnlyGuard block>
                <Card
                    className="fs-unmask"
                    style={{
                        border: `1px dashed ${isDragOver ? '#1777ff' : '#d9d9d9'}`,
                        borderRadius: '8px',
                        backgroundColor: isDragOver ? '#f0f8ff' : '#fafafa',
                        transition: 'all 0.3s ease',
                        cursor: role === 'Viewer' ? 'not-allowed' : 'pointer',
                        marginTop: '-15px',
                    }}
                    styles={{
                        body: {
                            paddingBottom: '15px',
                            paddingTop: '15px',
                        }
                    }}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    onClick={() => {
                        if (role !== 'Viewer' && fileInputRef.current) {
                            fileInputRef.current.click();
                        }
                    }}
                >
                    <div style={{
                        fontSize: '16px',
                        fontWeight: 500,
                        color: '#262626',
                        marginBottom: '8px',
                        textAlign: 'center',
                    }}>
                        External Model Results
                    </div>
                    <div style={{
                        color: '#8c8c8c',
                        fontSize: '14px',
                        fontWeight: 400,
                        lineHeight: '1.5',
                        textAlign: 'center',
                        marginBottom: '16px',
                    }}>
                        Upload external model predictions to compare against benchmark data
                    </div>
                    <div style={{ textAlign: 'center' }}>
                        <Upload
                            name="file"
                            multiple={false}
                            fileList={uploadedModelFileList}
                            beforeUpload={(file) => {
                                handleFileSelect(file);
                                return false;
                            }}
                            onRemove={() => { setUploadedModelFileList([]); }}
                            disabled={role === 'Viewer'}
                            showUploadList={false}
                        >
                            <Button
                                type="link"
                                icon={<DownloadOutlined />}
                                style={{
                                    color: colors.buttonsBlue,
                                    padding: 0,
                                    height: 'auto'
                                }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    if (role !== 'Viewer' && fileInputRef.current) {
                                        fileInputRef.current.click();
                                    }
                                }}
                            >
                                Upload External Model Results
                            </Button>
                        </Upload>
                    </div>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept={filesAllowed}
                        onChange={handleFileInputChange}
                        style={{ display: 'none' }}
                    />
                </Card>
            </ReadOnlyGuard>

            {/* External Model Results - File Origin/Columns Modal */}
            <DatasetUploadStepsModal
                visible={showPredictionsModal}
                onCancel={() => {
                    setShowPredictionsModal(false);
                    setPendingPredictionsFile(null);
                    setPredictionsSelectedOrigin(null);
                    setUploadedModelFileList([]);
                    setRenamedPredictionsFileName(null);
                }}
                fileName={renamedPredictionsFileName ? `${renamedPredictionsFileName}.${pendingPredictionsFile?.name?.split('.').pop()}` : pendingPredictionsFile?.name}
                selectedOrigin={predictionsSelectedOrigin}
                onOriginChange={setPredictionsSelectedOrigin}
                onSkip={() => handlePredictionsModalConfirm('skip')}
                onNext={() => handlePredictionsModalConfirm(predictionsSelectedOrigin)}
                onDone={(labelMappings = {}, exampleColumnName = "example", labelColumnName = "labels", relationsColumnName = "", relationsMappings = {}) => {
                    handlePredictionsModalConfirm(
                        predictionsSelectedOrigin,
                        labelMappings,
                        exampleColumnName,
                        labelColumnName,
                        relationsColumnName,
                        relationsMappings
                    );
                }}
                uploading={predictionsUploading}
                clientId={currentUsecase?.client_id}
                llmUsecaseId={currentUsecase?._id}
                allLabelOptions={allLabelOptions}
                uploadedFileName={renamedPredictionsFileName || pendingPredictionsFile?.name?.split('.')[0]}
                uploadedFileContent={predictionsFileContent}
                uploadedFileType={predictionsFileType}
                uploadedFile={pendingPredictionsFile}
                taskType={currentUsecase?.task_type}
                isMultilabel={currentUsecase?.is_multilabel}
                delimiter={predictionsDelimiter}
                setDelimiter={setPredictionsDelimiter}
            />

            {/* Dataset Exists Modal for external model results upload */}
            <DatasetExistsModal
                visible={showDatasetExistsModal}
                onCancel={() => {
                    setShowDatasetExistsModal(false);
                    setPendingPredictionsFile(null);
                    setPredictionsFileContent(null);
                    setPredictionsSelectedOrigin(null);
                    setUploadedModelFileList([]);
                    setRenamedPredictionsFileName(null);
                }}
                onUseExisting={() => {
                    setShowDatasetExistsModal(false);
                    setShowPredictionsModal(true);
                }}
                onRenameUpload={() => {
                    setShowDatasetExistsModal(false);
                    setShowRenameModal(true);
                }}
                fileName={pendingPredictionsFile?.name?.split('.')[0]}
                loading={predictionsUploading}
            />

            {/* Rename Upload Modal */}
            <RenameUploadModal
                visible={showRenameModal}
                onCancel={() => {
                    setShowRenameModal(false);
                    setPendingPredictionsFile(null);
                    setPredictionsFileContent(null);
                    setPredictionsSelectedOrigin(null);
                    setUploadedModelFileList([]);
                    setRenamedPredictionsFileName(null);
                }}
                onBack={() => {
                    setShowRenameModal(false);
                    setShowDatasetExistsModal(true);
                }}
                onDone={(newFileName) => {
                    setRenamedPredictionsFileName(newFileName);
                    setShowRenameModal(false);
                    setShowPredictionsModal(true);
                }}
                originalFileName={pendingPredictionsFile?.name?.split('.')[0]}
                fileExtension={pendingPredictionsFile?.name?.split('.').pop()}
                loading={predictionsUploading}
            />
        </>
    );
};

export default ExternalModelResultsUpload;

